import * as React from "react";
import { Command } from "cmdk";
import { RnSearch } from "@remi/icons/next";

import { useMedia } from "../../hooks";
import { cn } from "../../lib/utils";

const DropdownCommand = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof Command> & { ref?: React.Ref<React.ComponentRef<typeof Command>> }) => {
  const { isMobile } = useMedia();
  return (
    <Command
      ref={ref}
      className={cn(
        "flex h-full w-full flex-col overflow-hidden bg-background-primary text-text-secondary sm:min-w-[216px]",
        isMobile && "rounded-b-none",
        className,
      )}
      {...props}
    />
  );
};

DropdownCommand.displayName = "DropdownCommand";

interface DropdownCommandInputProps extends React.ComponentPropsWithoutRef<typeof Command.Input> {
  isMobile?: boolean;
  icon?: boolean;
  hidden?: boolean;
}

const DropdownCommandInput = ({
  ref,
  className,
  icon = false,
  hidden = false,
  isMobile,
  ...props
}: DropdownCommandInputProps & { ref?: React.Ref<React.ComponentRef<typeof Command.Input>> }) => {
  return (
    <div className={cn("flex items-center", isMobile && !hidden && "gap-1 px-3 pt-3 pb-1.5")} cmdk-input-wrapper="">
      {icon && <RnSearch className="mr-2 h-4 w-4 shrink-0 opacity-50" />}
      <Command.Input
        ref={ref}
        className={cn(
          "flex w-full border-border-primary bg-transparent text-xs outline-hidden placeholder:text-text-tertiary disabled:cursor-not-allowed disabled:opacity-50",
          !isMobile && "h-10 border-b px-2 py-3",
          isMobile &&
            "h-9 rounded-md border px-3 opacity-100 focus:border-border-darkplus focus:shadow-[0_0_0_2px_var(--colors-border-dark)] focus-visible:border-border-darkplus focus-visible:shadow-[0_0_0_2px_var(--colors-border-dark)]",
          // Not redundant with !w-0: px-2 floors the used width at 16px, so this stays hit-testable.
          hidden && "!w-0 pointer-events-none absolute min-w-0 opacity-0",
          className,
        )}
        tabIndex={hidden ? -1 : 0}
        aria-hidden={hidden}
        {...props}
      />
    </div>
  );
};
DropdownCommandInput.displayName = "DropdownCommandInput";

const DropdownCommandList = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof Command.List> & {
  ref?: React.Ref<React.ComponentRef<typeof Command.List>>;
}) => {
  const { isMobile } = useMedia();
  return (
    <Command.List
      ref={ref}
      className={cn(
        "overflow-y-auto overflow-x-hidden",
        isMobile ? "max-h-[calc(100vh-28rem)]" : "max-h-96",
        className,
      )}
      {...props}
    />
  );
};

DropdownCommandList.displayName = "DropdownCommandList";

const DropdownCommandEmpty = ({
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof Command.Empty> & {
  ref?: React.Ref<React.ComponentRef<typeof Command.Empty>>;
}) => (
  <Command.Empty
    ref={ref}
    className="mt-2 flex h-6 items-center p-2 py-6 text-center text-text-tertiary text-xs italic"
    {...props}
  />
);

DropdownCommandEmpty.displayName = "DropdownCommandEmpty";

const DropdownCommandGroup = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof Command.Group> & {
  ref?: React.Ref<React.ComponentRef<typeof Command.Group>>;
}) => (
  <Command.Group
    ref={ref}
    className={cn(
      "overflow-hidden px-2 py-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-xxs",
      "sm:p-1",
      className,
    )}
    {...props}
  />
);

DropdownCommandGroup.displayName = "DropdownCommandGroup";

const DropdownCommandSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof Command.Separator> & {
  ref?: React.Ref<React.ComponentRef<typeof Command.Separator>>;
}) => (
  <Command.Separator
    ref={ref}
    className={cn("h-px border-border-primary border-b", className)}
    alwaysRender
    {...props}
  />
);
DropdownCommandSeparator.displayName = "DropdownCommandSeparator";

const DropdownCommandItem = ({
  ref,
  className,
  id,
  ...props
}: React.ComponentPropsWithoutRef<typeof Command.Item> & {
  ref?: React.Ref<React.ComponentRef<typeof Command.Item>>;
}) => (
  <Command.Item
    ref={ref}
    id={id}
    data-testid={id}
    className={cn(
      "relative flex max-w-full cursor-default select-none items-center rounded-xs px-2 py-1.5 text-sm outline-hidden data-[disabled=true]:pointer-events-none data-[selected='true']:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50",
      className,
    )}
    {...props}
  />
);

DropdownCommandItem.displayName = "DropdownCommandItem";

export {
  DropdownCommand,
  DropdownCommandEmpty,
  DropdownCommandGroup,
  DropdownCommandInput,
  DropdownCommandItem,
  DropdownCommandList,
  DropdownCommandSeparator,
};
